// Conversion between Fabric.js objects and the normalized save format.
// All persisted coordinates are normalized to 0..1 of the canvas size so a save
// can be re-rendered at any display width.

import { FabricObject, Path, Point, Rect, Textbox, util } from "fabric";
import {
  createStamp,
  STAMP_ICON_URLS_TEACHER,
} from "@modules/annotation/service/image.annotation.stamp";
import type { StampObject } from "@modules/annotation/service/image.annotation.stamp";
import type { StrokeElement, AnnotationElement, BoundingBox } from "@modules/annotation/types";

// ---------------------------------------------------------------------------
// Fabric -> save format
// ---------------------------------------------------------------------------

export function fabricToElements(
  objects: FabricObject[],
  canvasW: number,
  canvasH: number,
  fallbackColor: string,
): AnnotationElement[] {
  const elements: AnnotationElement[] = [];
  objects.forEach((obj) => {
    const stamp = obj as StampObject;
    if (stamp.elementType === "stamp" && stamp.stampShape) {
      const scaleX = obj.scaleX ?? 1;
      const scaleY = obj.scaleY ?? 1;
      elements.push({
        type: "stamp",
        shape: stamp.stampShape,
        x: obj.left! / canvasW,
        y: obj.top! / canvasH,
        width: (Math.abs(obj.width ?? 0) * Math.abs(scaleX)) / canvasW,
        height: (Math.abs(obj.height ?? 0) * Math.abs(scaleY)) / canvasH,
        scaleX: Math.abs(scaleX),
        scaleY: Math.abs(scaleY),
        flipX: Boolean(obj.flipX || scaleX < 0),
        flipY: Boolean(obj.flipY || scaleY < 0),
      });
    } else if (obj instanceof Textbox) {
      const scaleX = obj.scaleX ?? 1;
      const scaleY = obj.scaleY ?? 1;
      elements.push({
        type: "text",
        text: obj.text ?? "",
        color: String(obj.fill ?? fallbackColor),
        x: obj.left! / canvasW,
        y: obj.top! / canvasH,
        width: (Math.abs(obj.width ?? 0) * Math.abs(scaleX)) / canvasW,
        scaleX: Math.abs(scaleX),
        scaleY: Math.abs(scaleY),
        flipX: Boolean(obj.flipX || scaleX < 0),
        flipY: Boolean(obj.flipY || scaleY < 0),
      });
    } else if (obj instanceof Path) {
      const flipX = Boolean(obj.flipX || (obj.scaleX ?? 1) < 0);
      const flipY = Boolean(obj.flipY || (obj.scaleY ?? 1) < 0);
      elements.push({
        type: "stroke",
        color: String(obj.stroke ?? fallbackColor),
        size: obj.strokeWidth ?? 5,
        points: extractStrokePoints(obj, canvasW, canvasH),
        flipX,
        flipY,
      });
    }
  });
  return elements;
}

export function extractStrokePoints(
  path: Path,
  canvasW: number,
  canvasH: number,
): [number, number][] {
  const matrix = path.calcTransformMatrix();
  const offset = path.pathOffset;
  const points: [number, number][] = [];
  (path.path as unknown as (string | number)[][]).forEach((cmd) => {
    for (let i = 1; i < cmd.length - 1; i += 2) {
      const x = cmd[i] as number;
      const y = cmd[i + 1] as number;
      if (typeof x !== "number" || typeof y !== "number") continue;
      const scene = util.transformPoint(new Point(x - offset.x, y - offset.y), matrix);
      points.push([+(scene.x / canvasW).toFixed(4), +(scene.y / canvasH).toFixed(4)]);
    }
  });
  return points;
}

// Union of every object's bounding rect, normalized to 0..1. Null when empty.
export function fabricBoundingBox(
  objects: FabricObject[],
  canvasW: number,
  canvasH: number,
): BoundingBox | null {
  if (objects.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  objects.forEach((obj) => {
    const r = obj.getBoundingRect();
    minX = Math.min(minX, r.left);
    minY = Math.min(minY, r.top);
    maxX = Math.max(maxX, r.left + r.width);
    maxY = Math.max(maxY, r.top + r.height);
  });
  return {
    x: +(minX / canvasW).toFixed(4),
    y: +(minY / canvasH).toFixed(4),
    width: +((maxX - minX) / canvasW).toFixed(4),
    height: +((maxY - minY) / canvasH).toFixed(4),
  };
}

// ---------------------------------------------------------------------------
// Save format -> fabric (read-only rendering)
// ---------------------------------------------------------------------------

// Builds non-interactive Fabric objects for a set of saved elements.
export function elementsToFabric(
  elements: AnnotationElement[],
  canvasW: number,
  canvasH: number,
  stampIcons: Record<string, string> = STAMP_ICON_URLS_TEACHER,
): FabricObject[] {
  const objects: FabricObject[] = [];
  elements.forEach((el) => {
    const obj = elementToFabric(el, canvasW, canvasH, stampIcons);
    if (obj) objects.push(obj);
  });
  return objects;
}

function elementToFabric(
  el: AnnotationElement,
  canvasW: number,
  canvasH: number,
  stampIcons: Record<string, string>,
): FabricObject | null {
  if (el.type === "stroke") return strokeToPath(el, canvasW, canvasH);

  if (el.type === "text") {
    const scaleX = Math.abs(el.scaleX ?? 1);
    const scaleY = Math.abs(el.scaleY ?? 1);
    return new Textbox(el.text, {
      left: el.x * canvasW,
      top: el.y * canvasH,
      originX: "center",
      originY: "center",
      width: el.width * canvasW,
      fontSize: 30,
      fontFamily: "Noto Sans, sans-serif",
      fill: el.color,
      scaleX,
      scaleY,
      flipX: Boolean(el.flipX),
      flipY: Boolean(el.flipY),
      ...readOnlyProps,
    });
  }

  // stamp
  const stamp = createStamp(el.shape, stampIcons);
  const scaleX = Math.abs(el.scaleX ?? 1);
  const scaleY = Math.abs(el.scaleY ?? 1);
  const flipX = Boolean(el.flipX);
  const flipY = Boolean(el.flipY);
  stamp.set({
    left: el.x * canvasW,
    top: el.y * canvasH,
    flipX,
    flipY,
    scaleX,
    scaleY,
  });
  const targetW = el.width * canvasW;
  const targetH = el.height * canvasH;
  const currentW = stamp.getScaledWidth() || 40;
  const currentH = stamp.getScaledHeight() || 40;
  stamp.scaleX = (stamp.scaleX ?? 1) * (targetW / currentW);
  stamp.scaleY = (stamp.scaleY ?? 1) * (targetH / currentH);
  stamp.set(readOnlyProps);
  stamp.setCoords();
  return stamp;
}

function strokeToPath(el: StrokeElement, canvasW: number, canvasH: number): Path | null {
  if (el.points.length === 0) return null;
  const d = el.points
    .map(([nx, ny], i) => {
      const x = (nx * canvasW).toFixed(2);
      const y = (ny * canvasH).toFixed(2);
      return `${i === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");
  const path = new Path(d, {
    fill: "",
    stroke: el.color,
    strokeWidth: el.size,
    strokeLineCap: "round",
    strokeLineJoin: "round",
    ...readOnlyProps,
  });
  path.set({
    flipX: Boolean(el.flipX),
    flipY: Boolean(el.flipY),
  });
  return path;
}

// A dashed rectangle Fabric object for a normalized bounding box.
export function boundingBoxRect(
  box: BoundingBox,
  canvasW: number,
  canvasH: number,
  color: string,
): Rect {
  return new Rect({
    left: box.x * canvasW,
    top: box.y * canvasH,
    width: box.width * canvasW,
    height: box.height * canvasH,
    originX: "left",
    originY: "top",
    fill: "",
    stroke: color,
    strokeWidth: 1.5,
    strokeDashArray: [6, 4],
    strokeUniform: true,
    ...readOnlyProps,
  });
}

const readOnlyProps = {
  selectable: false,
  evented: false,
  hoverCursor: "default",
} as const;
